import { UserRole } from 'types';

import { DOFIN_READ_ROLES } from '../Dofinansowanie/useDofinPermissions';

/**
 * Role sets that gate palette commands / actions and the search fan-out. These
 * mirror the visibility rules in `organisms/Menu/MenuContainer` and
 * `router/AppRouter#getRoutesByRole` so the palette never surfaces a destination
 * the current operator has no route to.
 */
export const STUDENT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
];

/**
 * Кто ПРАВИТ «Обучение» из палитры: создать группу и преподавателя, править
 * группу, опубликовать в LMS — и раздел «Преподаватели». Методиста здесь нет
 * намеренно: группу и журнал он видит только на чтение (ТЗ 0026), а
 * маршрута «Преподаватели» у него нет вовсе.
 */
export const EDUCATION_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

/**
 * ТЗ 0093: кто ОТКРЫВАЕТ «Обучение» — курсы, группы, журналы
 * (`/education/…`). Зеркало маршрута `education` в `AppRouter`: одна
 * константа на меню, палитру, `g → …` и саму страницу. До ТЗ 0093 у меню был
 * свой список с методистом, а у палитры — без него: методист видел пункт
 * меню, но не находил команду.
 */
export const EDUCATION_SECTION_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.METHODIST,
  UserRole.HRMANAGER,
];

export const FINANCE_VIEW_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

export const FINANCE_EXPENSE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
];

export const FINANCE_PLAN_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
];

export const ADMIN_ROLES: UserRole[] = [UserRole.ADMIN];

/**
 * ТЗ 0074 / 0093: настройки робота блокировки должников — зеркало
 * `PAYMENT_ACCESS_ROLES` бэкенда (Admin, FinanceManager, AccountManager,
 * SalesManager). Править можно тому, кому разрешит сервер (`canEdit`).
 */
export const PAYMENT_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.FINANCIER,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
];

/**
 * ТЗ 0023: «Подключённые ИИ» — зеркало `MANAGEMENT_ROLES` бэкенда, куда
 * входит и HR-менеджер (не `STUDENT_ROLES`).
 */
export const MCP_ACCESS_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.FINANCIER,
  UserRole.HRMANAGER,
];

export const ZOOM_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

// Survey constructor (spec 0011) — same managers who build/attach surveys and
// see results; mirrors the `/surveys` route + menu gate.
export const SURVEY_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.METHODIST,
];

// Career centre (spec 0018) — mirrors the `/career-center` route gate and the
// menu leaf. Note it carries HRManager, unlike SURVEY_ROLES.
export const CAREER_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.HRMANAGER,
  UserRole.METHODIST,
];

/**
 * Со-финансирование (ТЗ 0024) — зеркало `DOFIN_READ_ROLES` на бэкенде.
 *
 * Совпадает по составу со `STUDENT_ROLES`, но объявлено отдельно намеренно:
 * это разные правила, и когда одно из них поменяется, менять придётся именно
 * его, а не оба сразу. FinanceManager (`FINANCIER`) входит сюда **только на
 * чтение** — записи бэкенд ему не даёт.
 *
 * ТЗ 0093: это ТОТ ЖЕ список, что у `useDofinPermissions().canRead` и
 * раздела «Настройки → Письма дофинансирования», — не копия.
 */
export const DOFIN_ROLES: UserRole[] = DOFIN_READ_ROLES;

export const PERFORMANCE_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.HRMANAGER,
];

/**
 * ТЗ 0026: кто может ПРАВИТЬ отметку посещаемости.
 *
 * Зеркало ролей `updateAttendance` на бэкенде. Методиста здесь намеренно нет:
 * ему открыли журнал на чтение, и без этого списка он кликал бы по чекбоксу
 * и упирался в PermissionDenied.
 */
export const ATTENDANCE_EDIT_ROLES: UserRole[] = [
  UserRole.ADMIN,
  UserRole.ACCOUNTANT,
  UserRole.SALESMANAGER,
  UserRole.HRMANAGER,
];

/**
 * `true` when `allowed` is undefined (visible to any logged-in operator) or the
 * operator holds at least one of the allowed roles.
 */
export const visibleFor = (roles: UserRole[], allowed?: UserRole[]): boolean =>
  !allowed || roles.some((role) => allowed.includes(role));
